/**
 * Password Validation Utility
 *
 * Enforces strong password requirements:
 * - Minimum 10 characters
 * - At least 1 lowercase letter (a-z)
 * - At least 1 uppercase letter (A-Z)
 * - At least 1 special character: ! @ # $ % ^ & * ( ) _ + - = [ ] { } ; ' : " \ | , . < > / ?
 * - ASCII characters only (no unicode, emojis, accented characters)
 *
 * Shared across client-saas and admin applications.
 *
 * @param {string} password - The password to validate
 * @returns {string[]} - Array of error messages (empty if valid)
 *
 * @example
 * const errors = validatePassword('MyPass123!');
 * if (errors.length === 0) {
 *   console.log('Valid password');
 * } else {
 *   console.log('Password must have:', errors.join(', '));
 * }
 */
export function validatePassword(password) {
  const errors = [];

  // Check length
  if (password.length < 10) {
    errors.push('at least 10 characters');
  }

  // Check for lowercase
  if (!/[a-z]/.test(password)) {
    errors.push('1 lowercase');
  }

  // Check for uppercase
  if (!/[A-Z]/.test(password)) {
    errors.push('1 uppercase');
  }

  // Check for special character
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    errors.push('1 special character');
  }

  // Check for ASCII only (0-127)
  if (/[^\x00-\x7F]/.test(password)) {
    errors.push('ASCII characters only');
  }

  return errors;
}

/**
 * Check if password is valid
 * @param {string} password - The password to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export function isPasswordValid(password) {
  return validatePassword(password).length === 0;
}

/**
 * Format validation errors into user-friendly message
 * Phone-friendly: Max 2 lines (pipe-delimited for toast)
 * @param {string[]} errors - Array of error messages from validatePassword()
 * @returns {string} - Formatted error message
 */
export function formatPasswordErrors(errors) {
  if (errors.length === 0) {
    return '';
  }

  // Special case: Non-ASCII characters (emoji, unicode, etc.)
  if (errors.includes('ASCII characters only')) {
    return 'Password cannot contain emoji|or special unicode characters';
  }

  // Special case: Length issue
  if (errors.includes('at least 10 characters')) {
    return 'Password must have at least|10 characters';
  }

  // General case: Missing character types
  return 'Password must have: Uppercase,|Lowercase, and Special Character';
}
